import React from 'react';
import { Card, Box, Stack, Group, Avatar, Text, Badge, Flex } from '@mantine/core';
import { IoBook } from 'react-icons/io5';
import { FEED_ACTION_BAR_SX, FEED_CARD_HOVER } from './feedCardTheme';

export interface BlogDiscoverCardProps {
  title: string;
  excerpt: string;
  tags?: string[];
  userName: string;
  userAvatar?: string | null;
  timeLabel: string;
  thumbnailUrl?: string | null;
  onOpen?: () => void;
  actionBar?: React.ReactNode;
  maxTags?: number;
}

/**
 * Presentational blog post card for the mixed discover feed.
 */
export function BlogDiscoverCard ({
  title,
  excerpt,
  tags = [],
  userName,
  userAvatar,
  timeLabel,
  thumbnailUrl,
  onOpen,
  actionBar,
  maxTags = 5
}: BlogDiscoverCardProps) {
  const visibleTags = tags.slice(0, maxTags);
  const overflow = tags.length - visibleTags.length;

  return (
    <Card
      padding={0}
      radius="md"
      withBorder
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderColor: 'var(--gs-border)',
        backgroundColor: 'var(--gs-bg-2)',
        cursor: onOpen ? 'pointer' : undefined,
        ...FEED_CARD_HOVER
      }}
      onClick={onOpen}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(var(--gs-accent-rgb), 0.12)'
            }}
          >
            <IoBook size={48} color="var(--gs-accent)" />
          </Box>
        )}
      </Box>

      <Box sx={{ padding: '12px 14px' }}>
        <Text size="md" fw={600} lineClamp={2} mb="xs">
          {title}
        </Text>
        <Text size="sm" c="dimmed" lineClamp={2} mb="sm">
          {excerpt}
        </Text>

        {visibleTags.length > 0 && (
          <Flex gap={6} mb="md" wrap="wrap" onClick={(e) => e.stopPropagation()}>
            {visibleTags.map((tag) => (
              <Badge key={tag} size="sm" variant="light" color="gray">
                {tag}
              </Badge>
            ))}
            {overflow > 0 && (
              <Text size="xs" c="dimmed">
                +{overflow}
              </Text>
            )}
          </Flex>
        )}

        <Box
          sx={{
            borderTop: '1px solid var(--gs-border)',
            paddingTop: 12
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Group spacing="sm" noWrap>
            <Avatar src={userAvatar ?? undefined} alt={userName} size={32} radius="xl" />
            <Stack spacing={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {userName}
              </Text>
              <Text size="xs" c="dimmed">
                {timeLabel}
              </Text>
            </Stack>
          </Group>
        </Box>
      </Box>

      {actionBar && (
        <Group
          spacing="md"
          sx={FEED_ACTION_BAR_SX}
          align="center"
          wrap="nowrap"
          onClick={(e) => e.stopPropagation()}
        >
          {actionBar}
        </Group>
      )}
    </Card>
  );
}
